import { section } from "@/glow";
import Carousel from "@/lib/carousel";
import { checkMediaQuery } from "@/lib/media-queries";
import atBreakpointChange from "@/lib/at-breakpoint-change";
import { animateStatistics, shouldAnimate } from "@/lib/animation";

const selectors = {
  swiper: "[js-swiper]",
  statisticBlockNumber: "[js-statistic-block-number]",
  statisticNumberEl: "[js-statistic-number]",
  statisticDummyNumberEl: "[js-dummy-number]",
};

const attrs = {
  statisticNumberValue: "data-statistic-number-value",
  thousandsSeparatorType: "data-number-statistic-thousands-separator-type",
  decimalSeparatorType: "data-number-statistic-decimal-separator-type",
  countingAnimationEnabled: "data-counting-animation-enabled",
  animationDuration: "data-animation-duration",
  animationSpeed: "data-animation-speed",
  animationLinearity: "data-animation-linearity",
  animationStaggerDirection: "data-animation-stagger-direction",
  animationStaggerDelay: "data-animation-stagger-delay",
  statisticEndValue: "data-statistic-end-value",
  statisticStartValue: "data-statistic-start-value",
  decimalPlaces: "data-decimal-places",
  sliderOnMobile: "data-slider-on-mobile",
  mobileColumnsPerView: "data-mobile-columns-per-view",
  hasBeenActivated: "data-has-been-activated",
};

section("statistics", {
  onLoad() {
    this.eventController = new AbortController();
    this.eventOptions = { signal: this.eventController.signal };

    this.countingAnimationEnabled =
      this.container.getAttribute(attrs.countingAnimationEnabled) === "true";
    this.mobileCarouselEnabled =
      this.container.getAttribute(attrs.sliderOnMobile) === "true";
    this.hasNumberBlocks =
      this.container.querySelectorAll(selectors.statisticBlockNumber).length >
      0;

    if (this.hasNumberBlocks) {
      this.numberBlocks = this.container.querySelectorAll(
        selectors.statisticBlockNumber
      );
      this.initNumberBlocks(this.numberBlocks);

      if (this.countingAnimationEnabled) {
        this.setupGridObserver(this.numberBlocks);
      }
    }

    if (this.mobileCarouselEnabled) {
      this.breakPointHandler = atBreakpointChange(720, () => {
        const isMobile = checkMediaQuery("below-720", this.container);

        if (isMobile && !this.swiperInstance) {
          this.initMobileCarousel();
        } else if (!isMobile && this.swiperInstance) {
          this.cleanupMobileCarousel();
        }
      });

      const isMobile = checkMediaQuery("below-720", this.container);
      if (isMobile) {
        this.initMobileCarousel();
      }
    }

    if (shouldAnimate(this.container)) {
      this.animateStatistics = animateStatistics(this.container);
    }
  },

  initNumberBlocks(numberBlocks) {
    this.thousandsSeparatorType = this.container.getAttribute(
      attrs.thousandsSeparatorType
    );
    this.decimalSeparatorType = this.container.getAttribute(
      attrs.decimalSeparatorType
    );

    numberBlocks.forEach(block => {
      const statisticNumberEl = block.querySelector(
        selectors.statisticNumberEl
      );
      if (!statisticNumberEl) return;

      const statisticNumberValue = statisticNumberEl.getAttribute(
        attrs.statisticNumberValue
      );
      const statisticEndValue = parseFloat(statisticNumberValue);
      const decimalPlaces = statisticNumberValue.includes(".")
        ? statisticNumberValue.split(".")[1]?.length || 0
        : 0;

      const finalFormattedNumber = this.formatNumberWithSeparator(
        statisticEndValue,
        decimalPlaces
      );

      if (this.countingAnimationEnabled) {
        const statisticDummyNumberEl = block.querySelector(
          selectors.statisticDummyNumberEl
        );
        const statisticNumberDigitCount =
          statisticNumberValue.split(".")[0].length;
        const statisticStartValue =
          statisticNumberDigitCount >= 3
            ? Math.pow(10, statisticNumberDigitCount - 2)
            : 0;

        statisticNumberEl.setAttribute(
          attrs.statisticStartValue,
          statisticStartValue
        );
        statisticNumberEl.setAttribute(
          attrs.statisticEndValue,
          statisticEndValue
        );
        statisticNumberEl.setAttribute(attrs.decimalPlaces, decimalPlaces);

        // ensure dummy number has the correct formatting
        statisticDummyNumberEl.textContent = finalFormattedNumber;
      } else {
        statisticNumberEl.textContent = finalFormattedNumber;
      }
    });
  },

  formatNumberWithSeparator(value, decimalPlaces) {
    const fixedValue = Number(value).toFixed(decimalPlaces);
    const [integerRaw, decimalPart = ""] = fixedValue.split(".");
    const useThousandsSeparator = this.thousandsSeparatorType !== "none";
    let thousandsSeparator, decimalSeparator;

    switch (this.thousandsSeparatorType) {
      case "apostrophe":
        thousandsSeparator = "'";
        break;
      case "comma":
        thousandsSeparator = ",";
        break;
      case "period":
        thousandsSeparator = ".";
        break;
      case "space":
        thousandsSeparator = " ";
        break;
    }

    switch (this.decimalSeparatorType) {
      case "comma":
        decimalSeparator = ",";
        break;
      case "period":
        decimalSeparator = ".";
        break;
    }

    const integerPart =
      useThousandsSeparator && integerRaw.length >= 4
        ? integerRaw.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator)
        : integerRaw;

    return decimalPlaces > 0
      ? `${integerPart}${decimalSeparator}${decimalPart}`
      : integerPart;
  },

  easingFunction(x) {
    return x * x * x;
  },

  animateStatisticNumbers(numberStatisticsBlock) {
    const statisticNumberEl = numberStatisticsBlock.querySelector(
      selectors.statisticNumberEl
    );
    if (!statisticNumberEl) return;

    const statisticStartValue = Number(
      statisticNumberEl.getAttribute(attrs.statisticStartValue)
    );
    const statisticEndValue = Number(
      statisticNumberEl.getAttribute(attrs.statisticEndValue)
    );
    const decimalPlaces = Number(
      statisticNumberEl.getAttribute(attrs.decimalPlaces)
    );

    const valueRange = statisticEndValue - statisticStartValue;
    // Making speed effect more pronounced: Lower speed = larger interval (less smooth),
    // Higher speed = smaller interval (more smooth)
    const minInterval =
      this.animationDuration / Math.pow(this.animationSpeed, 1.5);

    const startTime = performance.now();
    let lastUpdateTime = startTime;

    const animate = () => {
      const now = performance.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / this.animationDuration, 1);
      const easingMultiplier =
        1 + this.easingFunction(progress) * this.animationLinearity;
      const interval = minInterval * easingMultiplier;
      const timeSinceLastUpdate = now - lastUpdateTime;

      if (timeSinceLastUpdate >= interval || progress >= 1) {
        const currentValue = statisticStartValue + valueRange * progress;
        statisticNumberEl.textContent = this.formatNumberWithSeparator(
          currentValue,
          decimalPlaces
        );
        lastUpdateTime = now;
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  },

  createObserver(numberBlocks, layoutType) {
    this.animationDuration = parseInt(
      this.container.getAttribute(attrs.animationDuration),
      10
    );
    this.animationSpeed = parseInt(
      this.container.getAttribute(attrs.animationSpeed),
      10
    );
    this.animationLinearity = parseInt(
      this.container.getAttribute(attrs.animationLinearity),
      10
    );
    this.animationStaggerDirection = this.container.getAttribute(
      attrs.animationStaggerDirection
    );

    if (this.animationStaggerDirection !== "none") {
      if (layoutType === "carousel") {
        this.animationStaggerDirection = "none";
      }

      this.animationStaggerDelay = parseInt(
        this.container.getAttribute(attrs.animationStaggerDelay),
        10
      );
    }

    const threshold = layoutType === "carousel" ? 0.4 : 0.15;

    const blocksArray = Array.from(numberBlocks);
    const orderedBlocks =
      this.animationStaggerDirection === "right"
        ? blocksArray.reverse()
        : blocksArray;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const blockIndex = orderedBlocks.indexOf(entry.target);
            const staggerDelay =
              this.animationStaggerDirection === "none"
                ? 0
                : blockIndex * this.animationStaggerDelay;

            setTimeout(() => {
              this.animateStatisticNumbers(entry.target);
            }, staggerDelay);

            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold,
      }
    );

    orderedBlocks.forEach(block => {
      observer.observe(block);
    });

    return observer;
  },

  setupGridObserver(numberBlocks) {
    this.gridObserver?.disconnect();
    this.gridObserver = this.createObserver(numberBlocks, "grid");
  },

  setupCarouselObserver(numberBlocks) {
    this.carouselObserver?.disconnect();
    this.carouselObserver = this.createObserver(numberBlocks, "carousel");
  },

  setupSwiper() {
    const swiperContainer = this.container.querySelector(selectors.swiper);
    const mobileColumnsPerView =
      parseInt(this.container.getAttribute(attrs.mobileColumnsPerView), 10) *
      1.05; // Peek value;

    this.swiperInstance = Carousel(swiperContainer, {
      slidesPerView: mobileColumnsPerView,
      spaceBetween: 12,
    });
  },

  initMobileCarousel() {
    this.setupSwiper();

    if (this.hasNumberBlocks) {
      const carouselNumberBlocks = this.container.querySelectorAll(
        `${selectors.swiper} ${selectors.statisticBlockNumber}`
      );
      this.initNumberBlocks(carouselNumberBlocks);

      if (this.countingAnimationEnabled) {
        this.setupCarouselObserver(carouselNumberBlocks);
      }
    }
  },

  cleanupMobileCarousel() {
    // Destroy Swiper instance
    this.swiperInstance?.destroy();
    this.swiperInstance = null;

    // Disconnect observer
    this.carouselObserver?.disconnect();
    this.carouselObserver = null;
  },

  onUnload() {
    this.eventController.abort();
    this.cleanupMobileCarousel();
    this.gridObserver?.disconnect();
    this.gridObserver = null;
    this.breakPointHandler?.unload();
    this.animateStatistics?.destroy();
  },
});
